import { useEffect } from 'react';

/**
 * Print isolation hook.
 *
 * Print routes in this app render INSIDE the portal layout (sidebar + header +
 * footer + gray page background, via <Outlet/>). A plain `window.print()` would
 * therefore include all of that chrome in the PDF. This hook isolates a single
 * element for printing without needing to know the layout's internal class
 * names: it walks from `rootRef` up to <body> and, at every level, marks the
 * element's siblings (sidebar/header/footer/etc.) with `print-hide` and the
 * ancestors with `print-expand`. Paired CSS (see the print pages) does:
 *
 *   @media print {
 *     .print-hide   { display: none !important; }
 *     .print-expand { height:auto!important; max-height:none!important; overflow:visible!important; }
 *   }
 *
 * Classes are added on the live DOM and removed on cleanup, and only take visual
 * effect under @media print — so the on-screen layout is never affected.
 *
 * Because content is left in normal flow (no absolute positioning), multi-page
 * invoices still paginate correctly.
 *
 * @param {React.RefObject<HTMLElement>} rootRef  element to keep when printing
 * @param {boolean} ready  re-run once async data has rendered the real DOM
 */
export default function usePrintIsolation(rootRef, ready = true) {
    useEffect(() => {
        if (!ready) return undefined;
        const root = rootRef.current;
        if (!root) return undefined;

        const hidden = [];
        const expanded = [];
        let el = root;

        while (el && el.parentElement && el !== document.body) {
            const current = el;
            const parent = current.parentElement;
            Array.from(parent.children).forEach((sib) => {
                if (sib !== current && sib.tagName !== 'STYLE' && sib.tagName !== 'SCRIPT') {
                    sib.classList.add('print-hide');
                    hidden.push(sib);
                }
            });
            parent.classList.add('print-expand');
            expanded.push(parent);
            el = parent;
        }

        return () => {
            hidden.forEach((s) => s.classList.remove('print-hide'));
            expanded.forEach((s) => s.classList.remove('print-expand'));
        };
    }, [rootRef, ready]);
}
